import type { ChangeEvent, FocusEvent } from 'react';
import { useState } from 'react';

// this should probably live in @balance-web/utils but I'm not totally sure about all the behaviour
// so I'm gonna leave it here for now.

type Config<ParsedValue> = {
  validate: (value: string) => boolean;
  parse: (value: string) => ParsedValue;
  format: (value: ParsedValue) => string;
  determineType: (value: string | ParsedValue) => 'raw' | 'parsed';
};

export function useFormattedInput<ParsedValue>(
  config: Config<ParsedValue>,
  {
    value,
    onChange,
    onBlur,
    onFocus,
  }: {
    value: string | ParsedValue;
    onChange: (value: ParsedValue | string) => void;
    onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  }
) {
  const [isFocused, setIsFocused] = useState(false);

  let valueType = config.determineType(value);
  if (valueType === 'raw' && config.validate(value as string)) {
    throw new Error(
      `Valid values must be passed in as a parsed value, not a raw value. The value you passed was \`${JSON.stringify(
        value
      )}\`, you should pass \`${JSON.stringify(
        config.parse(value as string)
      )}\` instead`
    );
  }

  let [internalValueState, setInternalValueState] = useState(() => {
    return valueType === 'raw'
      ? (value as string)
      : config.format(value as ParsedValue);
  });

  // Adding '&& value' prevents RWR-9181 happening, not the ideal fix though.
  if (valueType === 'raw' && value && value !== internalValueState) {
    setInternalValueState(value as string);
  }

  if (
    valueType === 'parsed' &&
    !isFocused &&
    config.format(value as ParsedValue) !== internalValueState
  ) {
    setInternalValueState(config.format(value as ParsedValue));
  }

  return {
    value: internalValueState,
    onChange(event: ChangeEvent<HTMLInputElement>) {
      const value = event.target.value;
      if (config.validate(value)) {
        onChange(config.parse(value));
        setInternalValueState(value);
      } else {
        onChange(value);
        setInternalValueState(value);
      }
    },
    onFocus(event: FocusEvent<HTMLInputElement>) {
      onFocus?.(event);
      setIsFocused(true);
    },
    onBlur(event: FocusEvent<HTMLInputElement>) {
      onBlur?.(event);
      setIsFocused(false);
      // this isn't strictly necessary since we already do this in render
      // this just saves another rerender after setIsFocused(false)
      if (valueType === 'parsed') {
        setInternalValueState(config.format(value as ParsedValue));
      }
    },
    /**
     * Because of the way this component is setup, it's very difficult to support
     *  onFocus external value sync.
     * This function allows consumer to set the value
     *  directly onFocus, bypassing a bunch restrictions. We still make sure the value
     *  passed in is correct but the consumer is responsible for making sure they send
     *  the same value to their state and setValueImperatively.
     */
    setValueImperatively: (value: any) => {
      let valueType = config.determineType(value);
      if (valueType === 'raw' && config.validate(value as string)) {
        throw new Error(
          `Valid values must be passed in as a parsed value, not a raw value. The value you passed was \`${JSON.stringify(
            value
          )}\`, you should pass \`${JSON.stringify(
            config.parse(value as string)
          )}\` instead`
        );
      }
      setInternalValueState(config.format(value as ParsedValue));
    },
  };
}

export const getCurrencyFormatter = (args: {
  maximumFractionDigits?: 0 | 2 | 4 | 5 | 8;
  minimumFractionDigits?: 2 | 4 | 5 | 8;
}) => {
  const maximumFractionDigits =
    args.maximumFractionDigits === undefined ? 2 : args.maximumFractionDigits;
  const minimumFractionDigits =
    args.minimumFractionDigits === undefined ? 2 : args.minimumFractionDigits;

  return Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    currencyDisplay: 'symbol',
    maximumFractionDigits: maximumFractionDigits,
    minimumFractionDigits:
      maximumFractionDigits === 0
        ? 0
        : Math.min(maximumFractionDigits, minimumFractionDigits),
  });
};
